import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Box, VStack, HStack, Text, Heading, Badge, Button,
  Container, Flex, Icon, Table, Thead, Tbody, Tr, Th, Td,
  Divider, Spinner, useToast,
} from '@chakra-ui/react';
import {
  FaCreditCard, FaCheckCircle, FaLock, FaVial,
  FaSyringe, FaUserMd, FaCalendarAlt, FaReceipt,
  FaHospital, FaPhoneAlt, FaExclamationTriangle,
} from 'react-icons/fa';

const formatCurrency = (amount) => `$${parseFloat(amount || 0).toFixed(2)}`;
const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const PatientPaymentPage = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paying, setPaying] = useState(false);
  const toast = useToast();

  const paymentResult = searchParams.get('payment'); // 'success' | 'cancel' | null
  const paymentSuccess = paymentResult === 'success';
  const paymentCancelled = paymentResult === 'cancel';

  const apiBase = process.env.REACT_APP_API_URL
    || (window.location.hostname === 'localhost' ? '' : '');

  const fetchOrder = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${apiBase}/api/patient-payment?token=${encodeURIComponent(token)}`);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Order not found');
      }
      const data = await res.json();
      setOrder(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, apiBase]);

  useEffect(() => {
    if (token) fetchOrder();
  }, [token, fetchOrder]);

  // After Stripe redirect with success, poll until webhook confirms payment
  useEffect(() => {
    if (!paymentSuccess || !token) return;
    let attempts = 0;
    const maxAttempts = 15;
    let cancelled = false;
    const controller = new AbortController();

    const poll = async () => {
      while (attempts < maxAttempts && !cancelled) {
        attempts++;
        await new Promise(r => setTimeout(r, 2000));
        if (cancelled) return;
        try {
          const res = await fetch(
            `${apiBase}/api/patient-payment?token=${encodeURIComponent(token)}`,
            { signal: controller.signal }
          );
          if (res.ok) {
            const data = await res.json();
            if (data.payment_status === 'paid') {
              if (!cancelled) setOrder(data);
              return;
            }
          }
        } catch (e) {
          if (e.name === 'AbortError') return;
          // Network error — continue polling
        }
      }
      // After all attempts, show processing message instead of faking paid
      if (!cancelled) {
        setOrder(prev => prev ? { ...prev, payment_status: 'processing' } : prev);
      }
    };

    poll();
    return () => { cancelled = true; controller.abort(); };
  }, [paymentSuccess, token, apiBase]);

  const handlePayNow = async () => {
    if (!order?.draw_code) return;
    setPaying(true);
    try {
      const res = await fetch(`${apiBase}/api/create-selfpay-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ draw_code: order.draw_code }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error?.message || 'Failed to create checkout session');
      if (!data.url) throw new Error('No checkout URL returned');
      // Redirect to Stripe Checkout
      window.location.href = data.url;
    } catch (err) {
      const isNoPricing = err.message?.toLowerCase().includes('zero') || err.message?.toLowerCase().includes('no charges');
      toast({
        title: isNoPricing ? 'Pricing Not Available' : 'Payment Error',
        description: isNoPricing
          ? 'Test pricing has not been set for this order yet. Please contact your provider.'
          : err.message,
        status: isNoPricing ? 'warning' : 'error',
        duration: 7000,
      });
      setPaying(false);
    }
  };

  if (loading) {
    return (
      <Flex minH="100vh" align="center" justify="center" bg="gray.50">
        <VStack spacing={4}>
          <Spinner size="xl" color="teal.500" thickness="4px" />
          <Text color="gray.500" fontSize="sm">Loading order details...</Text>
        </VStack>
      </Flex>
    );
  }

  if (error || !order) {
    return (
      <Flex minH="100vh" align="center" justify="center" bg="gray.50">
        <Box bg="white" p={10} borderRadius="2xl" boxShadow="xl" maxW="md" w="full" textAlign="center">
          <Icon as={FaExclamationTriangle} boxSize={14} color="orange.300" mb={4} />
          <Heading size="md" color="gray.700" mb={2}>Order Not Found</Heading>
          <Text color="gray.500" fontSize="sm">
            {error || 'This payment link may be invalid or expired.'}
          </Text>
        </Box>
      </Flex>
    );
  }

  const isPaid = order.payment_status === 'paid';
  const isProcessing = order.payment_status === 'processing';

  return (
    <Box bg="gray.50" minH="100vh">
      {/* Processing Banner */}
      {isProcessing && (
        <Box bg="blue.500" py={2} textAlign="center">
          <Text color="white" fontSize="sm" fontWeight="700">
            Payment received! Confirming with our system — this may take a moment.
          </Text>
        </Box>
      )}
      {/* Cancel Banner */}
      {paymentCancelled && !isPaid && !isProcessing && (
        <Box bg="orange.400" py={2} textAlign="center">
          <Text color="white" fontSize="sm" fontWeight="700">
            Payment was cancelled. You can try again below.
          </Text>
        </Box>
      )}
      <Container maxW="700px" py={8} px={4}>
        {/* Header */}
        <Box bg="teal.600" borderRadius="2xl" p={6} mb={6} color="white" position="relative" overflow="hidden">
          <Box position="absolute" top={-4} right={-4} opacity={0.1}>
            <Icon as={FaSyringe} boxSize={32} />
          </Box>
          <VStack spacing={1} align="start">
            <Text fontSize="sm" fontWeight="600" opacity={0.9} letterSpacing="wide">
              AtHomeBloodDraw.com
            </Text>
            <Heading size="lg" fontWeight="800">
              BLOOD DRAW PAYMENT
            </Heading>
            <Text fontSize="sm" opacity={0.8}>
              Mobile Phlebotomy Services
            </Text>
          </VStack>
          <Flex mt={4} justify="space-between" align="center">
            <VStack align="start" spacing={0}>
              <Text fontSize="xs" opacity={0.7} textTransform="uppercase">Patient</Text>
              <Text fontSize="md" fontWeight="700">{order.patient_display_name}</Text>
            </VStack>
            <VStack align="end" spacing={0}>
              <Text fontSize="xs" opacity={0.7} textTransform="uppercase">Order Date</Text>
              <Text fontSize="md" fontWeight="700">{formatDate(order.submitted_at)}</Text>
            </VStack>
          </Flex>
        </Box>

        {/* Status Badge */}
        <Flex justify="center" mb={6}>
          <Badge
            colorScheme={isPaid ? 'green' : 'orange'}
            fontSize="md"
            px={6}
            py={2}
            borderRadius="full"
            fontWeight="700"
          >
            <HStack spacing={2}>
              <Icon as={isPaid ? FaCheckCircle : FaReceipt} />
              <Text>{isPaid ? 'PAID' : 'PAYMENT DUE'}</Text>
            </HStack>
          </Badge>
        </Flex>

        {/* Order Details Card */}
        <Box bg="white" borderRadius="2xl" boxShadow="md" border="1px solid" borderColor="gray.100" mb={6} overflow="hidden">
          <Box px={6} py={4} bg="gray.50" borderBottom="1px solid" borderColor="gray.100">
            <HStack spacing={2}>
              <Icon as={FaVial} color="teal.500" />
              <Text fontSize="md" fontWeight="700" color="gray.700">Order Details</Text>
            </HStack>
          </Box>
          <Box px={6} py={4}>
            <VStack spacing={3} align="stretch">
              {order.doctor_name && (
                <HStack justify="space-between">
                  <HStack spacing={2}>
                    <Icon as={FaUserMd} color="gray.400" boxSize={3} />
                    <Text fontSize="sm" color="gray.500">Ordering Physician</Text>
                  </HStack>
                  <Text fontSize="sm" fontWeight="600">{order.doctor_name}</Text>
                </HStack>
              )}
              {order.lab_name && (
                <HStack justify="space-between">
                  <HStack spacing={2}>
                    <Icon as={FaHospital} color="gray.400" boxSize={3} />
                    <Text fontSize="sm" color="gray.500">Laboratory</Text>
                  </HStack>
                  <Text fontSize="sm" fontWeight="600">{order.lab_name}</Text>
                </HStack>
              )}
              {order.phlebotomist_name && (
                <HStack justify="space-between">
                  <HStack spacing={2}>
                    <Icon as={FaPhoneAlt} color="gray.400" boxSize={3} />
                    <Text fontSize="sm" color="gray.500">Phlebotomist</Text>
                  </HStack>
                  <Text fontSize="sm" fontWeight="600">{order.phlebotomist_name}</Text>
                </HStack>
              )}
              {order.collection_date && (
                <HStack justify="space-between">
                  <HStack spacing={2}>
                    <Icon as={FaCalendarAlt} color="gray.400" boxSize={3} />
                    <Text fontSize="sm" color="gray.500">Collection Date</Text>
                  </HStack>
                  <Text fontSize="sm" fontWeight="600">{formatDate(order.collection_date)}</Text>
                </HStack>
              )}
            </VStack>
          </Box>
        </Box>

        {/* Charges Breakdown */}
        <Box bg="white" borderRadius="2xl" boxShadow="md" border="1px solid" borderColor="gray.100" mb={6} overflow="hidden">
          <Box px={6} py={4} bg="gray.50" borderBottom="1px solid" borderColor="gray.100">
            <HStack spacing={2}>
              <Icon as={FaReceipt} color="teal.500" />
              <Text fontSize="md" fontWeight="700" color="gray.700">Charges</Text>
            </HStack>
          </Box>
          <Box px={6} py={4}>
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th px={0} fontSize="xs" color="gray.500">Description</Th>
                  <Th px={0} isNumeric fontSize="xs" color="gray.500">Amount</Th>
                </Tr>
              </Thead>
              <Tbody>
                {order.draw_fee > 0 && (
                  <Tr>
                    <Td px={0} py={3} borderColor="gray.100">
                      <HStack spacing={2}>
                        <Icon as={FaSyringe} color="teal.400" boxSize={3} />
                        <Text fontSize="sm" fontWeight="600">Blood Draw Service Fee</Text>
                      </HStack>
                    </Td>
                    <Td px={0} py={3} isNumeric borderColor="gray.100">
                      <Text fontSize="sm" fontWeight="600">{formatCurrency(order.draw_fee)}</Text>
                    </Td>
                  </Tr>
                )}
                {(order.tests || []).map((test, i) => (
                  <Tr key={i}>
                    <Td px={0} py={3} borderColor="gray.100">
                      <HStack spacing={2}>
                        <Icon as={FaVial} color="blue.400" boxSize={3} />
                        <Text fontSize="sm">{test.name}</Text>
                      </HStack>
                    </Td>
                    <Td px={0} py={3} isNumeric borderColor="gray.100">
                      <Text fontSize="sm">{formatCurrency(test.price)}</Text>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
            <Divider my={3} />
            <Flex justify="space-between" align="center">
              <Text fontSize="lg" fontWeight="800" color="gray.800">Total Due</Text>
              <Text fontSize="2xl" fontWeight="800" color={isPaid ? 'green.500' : 'teal.600'}>
                {formatCurrency(order.total_amount)}
              </Text>
            </Flex>
          </Box>
        </Box>

        {/* Payment Section */}
        {!isPaid && !isProcessing ? (
          order.total_amount > 0 ? (
            <Box bg="white" borderRadius="2xl" boxShadow="md" border="1px solid" borderColor="gray.100" mb={6} overflow="hidden">
              <Box px={6} py={4} bg="teal.50" borderBottom="1px solid" borderColor="teal.100">
                <HStack spacing={2}>
                  <Icon as={FaCreditCard} color="teal.600" />
                  <Text fontSize="md" fontWeight="700" color="teal.700">Pay Now</Text>
                </HStack>
              </Box>
              <Box px={6} py={5}>
                <VStack spacing={4}>
                  <Button
                    colorScheme="teal"
                    size="lg"
                    w="full"
                    leftIcon={<FaCreditCard />}
                    onClick={handlePayNow}
                    isLoading={paying}
                    loadingText="Redirecting to Stripe..."
                    fontWeight="700"
                    fontSize="md"
                    py={7}
                    borderRadius="xl"
                    boxShadow="md"
                  >
                    Pay {formatCurrency(order.total_amount)} with Card
                  </Button>
                  <HStack spacing={1} justify="center">
                    <Icon as={FaLock} color="gray.400" boxSize={3} />
                    <Text fontSize="xs" color="gray.400">Secured by Stripe — 256-bit SSL encryption</Text>
                  </HStack>
                </VStack>
              </Box>
            </Box>
          ) : (
            <Box bg="yellow.50" borderRadius="2xl" boxShadow="md" border="1px solid" borderColor="yellow.200" mb={6} p={6} textAlign="center">
              <Icon as={FaExclamationTriangle} color="yellow.500" boxSize={10} mb={3} />
              <Heading size="md" color="yellow.700" mb={2}>Pricing Unavailable</Heading>
              <Text fontSize="sm" color="yellow.600">
                Please contact us at support@athomeblooddraw.com to complete your payment.
              </Text>
            </Box>
          )
        ) : (
          <Box bg="green.50" borderRadius="2xl" boxShadow="md" border="2px solid" borderColor="green.200" mb={6} p={6} textAlign="center">
            <Icon as={FaCheckCircle} color="green.500" boxSize={12} mb={3} />
            <Heading size="md" color="green.700" mb={2}>Payment Complete</Heading>
            <Text fontSize="sm" color="green.600">
              Thank you! Your payment of {formatCurrency(order.total_amount)} has been received.
            </Text>
            {order.patient_email_masked && (
              <Text fontSize="xs" color="green.500" mt={2}>
                A receipt has been sent to {order.patient_email_masked}
              </Text>
            )}
          </Box>
        )}

        {/* Footer */}
        <VStack spacing={2} mt={8}>
          <Text fontSize="xs" color="gray.400" textAlign="center">
            AtHomeBloodDraw.com — Mobile Phlebotomy Services
          </Text>
          <Text fontSize="xs" color="gray.400" textAlign="center">
            Questions? Contact us at support@athomeblooddraw.com
          </Text>
        </VStack>
      </Container>
    </Box>
  );
};

export default PatientPaymentPage;
